/**
 * Building blocks for client side rate limiting.
 *
 * - {@link RateLimitGate} remembers a `Retry-After` cool-down for one resource
 *   (typically an endpoint) and lets everyone wait for it.
 * - {@link RequestQueue} is a FIFO semaphore: at most `maxConcurrent` tasks run
 *   at the same time, and a task whose gate is closed never takes a slot.
 * - {@link runRateLimited} ties both together: it runs a request through the
 *   queue and, on `429`, closes the gate for the `Retry-After` window before
 *   retrying — so queued *and* retried requests always back off together and
 *   never exceed the concurrency limit.
 */

import { backgroundDelay } from "@app/utils/workerTimer";

/** Fallback cool-down when the server does not send a `Retry-After` header. */
export const DEFAULT_RETRY_AFTER_MS = 5000;

/** Upper bound for a server requested cool-down, so the UI can never hang. */
export const MAX_RETRY_AFTER_MS = 60000;

/** Default number of times a rate limited request is retried. */
export const DEFAULT_MAX_RATE_LIMIT_RETRIES = 5;

const HTTP_TOO_MANY_REQUESTS = 429;

const createAbortError = () => {
  const error = new Error("Request aborted");

  error.name = "AbortError";
  // Matches the apisauce shape the query client checks to skip retries.
  error.cause = { problem: "CANCEL_ERROR" };

  return error;
};

const sleep = (ms, signal) =>
  backgroundDelay(ms, { signal }).catch(() => {
    throw createAbortError();
  });

const readHeader = (headers, name) => {
  if (!headers) {
    return undefined;
  }

  // Axios v1 exposes `AxiosHeaders`, other callers may pass a plain object.
  if (typeof headers.get === "function") {
    return headers.get(name);
  }

  const lowerName = name.toLowerCase();
  const key = Object.keys(headers).find((k) => k.toLowerCase() === lowerName);

  return key ? headers[key] : undefined;
};

/**
 * Converts a `Retry-After` value (delta seconds or HTTP date) to milliseconds.
 *
 * @param {string|number} value - The raw header/body value.
 * @returns {number|null} - The delay in milliseconds, or `null` when unusable.
 */
export const parseRetryAfterMs = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const seconds = Number(value);

  if (Number.isFinite(seconds)) {
    return Math.max(0, seconds * 1000);
  }

  const date = Date.parse(value);

  return Number.isNaN(date) ? null : Math.max(0, date - Date.now());
};

/**
 * Extracts the rate-limit cool-down from a response or an error cause.
 *
 * @param {object} response - An apisauce/axios response (or an `error.cause`).
 * @returns {number|null} - The delay in milliseconds, or `null` when unknown.
 */
export const getRetryAfterMs = (response) => {
  if (!response) {
    return null;
  }

  const candidates = [
    readHeader(response.headers, "Retry-After"),
    response.retryAfter,
    response.data?.retryAfter,
    response.data?.retry_after,
  ];

  for (const candidate of candidates) {
    const retryAfterMs = parseRetryAfterMs(candidate);

    if (retryAfterMs !== null) {
      return retryAfterMs;
    }
  }

  return null;
};

/**
 * Whether a response is a rate-limit rejection.
 *
 * @param {object} response - An apisauce/axios response.
 * @returns {boolean}
 */
export const isRateLimited = (response) =>
  response?.status === HTTP_TOO_MANY_REQUESTS;

/** Remembers a `Retry-After` cool-down shared by everyone using a resource. */
export class RateLimitGate {
  #name;
  #maxRetryAfterMs;
  #resumeAt = 0;

  constructor({ name = "gate", maxRetryAfterMs = MAX_RETRY_AFTER_MS } = {}) {
    this.#name = name;
    this.#maxRetryAfterMs = maxRetryAfterMs;
  }

  get name() {
    return this.#name;
  }

  /** Remaining cool-down in milliseconds (`0` when open). */
  get pausedForMs() {
    return Math.max(0, this.#resumeAt - Date.now());
  }

  /**
   * Closes the gate for `ms`, never shortening an existing cool-down.
   *
   * @param {number} ms - Requested cool-down in milliseconds.
   * @returns {number} - The effective cool-down remaining after this call.
   */
  pause(ms) {
    const delay = Math.min(Math.max(0, ms ?? 0), this.#maxRetryAfterMs);

    if (delay > 0) {
      this.#resumeAt = Math.max(this.#resumeAt, Date.now() + delay);
    }

    return this.pausedForMs;
  }

  /**
   * Waits until the gate is open again.
   *
   * @param {AbortSignal} [signal] - Aborts the wait.
   */
  async waitForResume(signal) {
    let remaining = this.pausedForMs;

    while (remaining > 0) {
      await sleep(remaining, signal);
      remaining = this.pausedForMs;
    }
  }
}

/**
 * FIFO semaphore. Tasks that cannot run yet are parked in a promise instead of
 * failing, so callers (and their react-query retries) never turn a local limit
 * into a burst of requests.
 */
export class RequestQueue {
  #name;
  #maxConcurrent;
  #active = 0;
  #waiters = [];
  #wakeAbort = null;

  constructor({ name = "request-queue", maxConcurrent = 1 } = {}) {
    this.#name = name;
    this.#maxConcurrent = Math.max(1, maxConcurrent);
  }

  get name() {
    return this.#name;
  }

  get maxConcurrent() {
    return this.#maxConcurrent;
  }

  /** Number of tasks currently holding a slot. */
  get activeCount() {
    return this.#active;
  }

  /** Number of tasks waiting for a slot. */
  get pendingCount() {
    return this.#waiters.length;
  }

  /**
   * Waits for a free slot. The slot is reserved synchronously when the queue
   * hands it out, so concurrency holds even before the caller resumes.
   *
   * A waiter with a closed `gate` is skipped until its cool-down elapses: it
   * neither takes a slot nor blocks requests to other endpoints behind it.
   *
   * @param {object} [options] - `signal` to abort, `gate` to rate limit.
   * @returns {Promise<void>}
   */
  acquire({ signal, gate } = {}) {
    if (signal?.aborted) {
      return Promise.reject(createAbortError());
    }

    return new Promise((resolve, reject) => {
      const waiter = { gate };

      const onAbort = () => {
        const index = this.#waiters.indexOf(waiter);

        if (index >= 0) {
          this.#waiters.splice(index, 1);
        }

        reject(createAbortError());
      };

      waiter.start = () => {
        signal?.removeEventListener("abort", onAbort);
        resolve();
      };

      this.#waiters.push(waiter);
      signal?.addEventListener("abort", onAbort, { once: true });
      this.#dispatch();
    });
  }

  /** Releases a slot previously handed out by `acquire`. */
  release() {
    if (this.#active > 0) {
      this.#active -= 1;
    }

    this.#dispatch();
  }

  /**
   * Runs `task` once a slot is free, releasing the slot afterwards.
   *
   * @param {Function} task - The work to run.
   * @param {object} [options] - `signal` to abort, `gate` to rate limit.
   * @returns {Promise<*>} - Whatever `task` resolves with.
   */
  async run(task, options = {}) {
    await this.acquire(options);

    try {
      return await task();
    } finally {
      this.release();
    }
  }

  #dispatch() {
    if (this.#wakeAbort) {
      this.#wakeAbort.abort();
      this.#wakeAbort = null;
    }

    let nextWakeUp = Infinity;
    let index = 0;

    while (index < this.#waiters.length && this.#active < this.#maxConcurrent) {
      const waiter = this.#waiters[index];
      const pausedForMs = waiter.gate?.pausedForMs ?? 0;

      if (pausedForMs > 0) {
        // Rate limited: leave it queued and look at the next waiter.
        nextWakeUp = Math.min(nextWakeUp, pausedForMs);
        index += 1;
        continue;
      }

      this.#waiters.splice(index, 1);
      this.#active += 1;
      waiter.start();
    }

    if (Number.isFinite(nextWakeUp)) {
      const controller = new AbortController();

      this.#wakeAbort = controller;

      backgroundDelay(nextWakeUp, { signal: controller.signal })
        .then(() => {
          this.#wakeAbort = null;
          this.#dispatch();
        })
        .catch(() => {
          // Superseded by a newer dispatch that aborted this wake-up.
        });
    }
  }
}

/**
 * Runs a request through `queue` and retries it while the server answers
 * `429`. The `Retry-After` window is applied to `gate` *before* the slot is
 * released, so every other request behind the same gate waits it out, and the
 * retry goes back through the queue and therefore respects `maxConcurrent`.
 *
 * @param {RequestQueue} queue - The concurrency limiter.
 * @param {RateLimitGate} gate - The cool-down shared by this endpoint.
 * @param {Function} request - Returns an apisauce-style response.
 * @param {object} [options] - `signal`, `maxRetries` and `onRateLimited`.
 * @returns {Promise<object>} - The last response (possibly still a `429`).
 */
export const runRateLimited = async (queue, gate, request, options = {}) => {
  const {
    signal,
    maxRetries = DEFAULT_MAX_RATE_LIMIT_RETRIES,
    onRateLimited,
  } = options;

  for (let attempt = 0; ; attempt += 1) {
    const response = await queue.run(
      async () => {
        const result = await request(attempt);

        if (isRateLimited(result)) {
          // Closing the gate before the slot is released guarantees that no
          // queued request slips through during the cool-down.
          const waitMs = gate.pause(
            getRetryAfterMs(result) ?? DEFAULT_RETRY_AFTER_MS,
          );

          console.warn(
            `[${queue.name}] rate limited (429) on "${gate.name}", holding ` +
              `back ${queue.pendingCount} queued request(s) for ${waitMs}ms ` +
              `(attempt ${attempt + 1}/${maxRetries + 1})`,
          );

          onRateLimited?.({ attempt, waitMs, response: result });
        }

        return result;
      },
      { signal, gate },
    );

    if (!isRateLimited(response) || attempt >= maxRetries) {
      return response;
    }
  }
};

/**
 * Returns an entry per `key`, creating it with `factory` on first use.
 *
 * @param {Map<string, *>} registry - Where the entries are cached.
 * @param {string} key - Identifier, e.g. an endpoint path.
 * @param {Function} factory - Builds the entry for a missing `key`.
 * @returns {*}
 */
export const getOrCreate = (registry, key, factory) => {
  let entry = registry.get(key);

  if (!entry) {
    entry = factory(key);
    registry.set(key, entry);
  }

  return entry;
};
